import { IDENTITY, type AdjustmentKind, type AdjustmentOf, type AdjustmentParams, type Document, type Mask, type Node } from "./document";

/**
 * Layer masks, clipping masks and adjustment layers: defaults, labels and the pure maths the
 * renderer and SVG exporter share (tested in masks.test.ts).
 */

/** A fresh mask that reveals everything (parts are added by the caller). */
export function defaultMask(partial: Partial<Mask> = {}): Mask {
  return { transform: [...IDENTITY], raster: null, vector: null, gradient: null, density: 1, feather: 0, invert: false, enabled: true, linked: true, ...partial };
}

/** True if the mask has anything that can hide pixels. */
export const maskHasContent = (m: Mask | null | undefined): m is Mask => !!m && (!!m.raster || !!m.vector || !!m.gradient);

/** The mask the renderer should apply (null when absent, disabled or empty). */
export const activeMask = (n: Node): Mask | null => (n.mask && n.mask.enabled && maskHasContent(n.mask) ? n.mask : null);

/**
 * Final mask alpha as an affine map of the raw (feathered) coverage `c`: alpha = a·c + b.
 * Invert flips the coverage, then density scales how much it hides: 1 − d·(1 − c').
 */
export function maskAlphaAffine(m: Pick<Mask, "invert" | "density">): { a: number; b: number } {
  let a = 1, b = 0;
  if (m.invert) (a = -1), (b = 1);
  const d = Math.min(1, Math.max(0, m.density));
  return { a: a * d, b: b * d + (1 - d) };
}

/**
 * Raw coverage of one mask point from its parts (each 0..1, absent = 1): the raster stores how
 * much it hides, so it contributes (1 − hide); parts multiply.
 */
export function maskCoverage(parts: { rasterHide?: number; vector?: number; gradient?: number }): number {
  return (1 - (parts.rasterHide ?? 0)) * (parts.vector ?? 1) * (parts.gradient ?? 1);
}

/** Final alpha multiplier at a point (feathering aside). */
export function maskAlpha(m: Pick<Mask, "invert" | "density" | "enabled">, coverage: number): number {
  if (!m.enabled) return 1;
  const { a, b } = maskAlphaAffine(m);
  return Math.min(1, Math.max(0, a * coverage + b));
}

/** Gradient fade coverage at mask-space point p: 1 at `start`, 0 at `end`, clamped. */
export function gradientCoverage(g: { start: [number, number]; end: [number, number] }, p: [number, number]): number {
  const dx = g.end[0] - g.start[0], dy = g.end[1] - g.start[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return 1;
  const t = ((p[0] - g.start[0]) * dx + (p[1] - g.start[1]) * dy) / len2;
  return 1 - Math.min(1, Math.max(0, t));
}

/** Ids of nodes used as another node's vector mask (they aren't drawn on their own). */
export function maskSourceIds(doc: Pick<Document, "nodes">): Set<string> {
  const out = new Set<string>();
  for (const n of Object.values(doc.nodes)) if (n.mask?.vector?.type === "node" && doc.nodes[n.mask.vector.nodeId] && n.mask.vector.nodeId !== n.id) out.add(n.mask.vector.nodeId);
  return out;
}

/**
 * Split a container's children (bottom → top) into draw runs: a base followed by the clipping
 * nodes stacked directly on it. A clipping node with nothing below it draws on its own.
 */
export function clipRuns(doc: Pick<Document, "nodes">, ids: readonly string[]): { base: string; clipped: string[] }[] {
  const runs: { base: string; clipped: string[] }[] = [];
  for (const id of ids) {
    const n = doc.nodes[id];
    const last = runs[runs.length - 1];
    if (n?.clip && last) last.clipped.push(id);
    else runs.push({ base: id, clipped: [] });
  }
  return runs;
}

/** The node a clipping node clips to (null if it doesn't clip or has no base). */
export function clipBase(doc: Pick<Document, "nodes">, siblings: readonly string[], id: string): string | null {
  if (!doc.nodes[id]?.clip) return null;
  const run = clipRuns(doc, siblings).find((r) => r.clipped.includes(id));
  return run?.base ?? null;
}

/**
 * Nodes an adjustment layer recolours: every sibling drawn before it (and their descendants
 * through the draw order). A clipping adjustment only affects its clipping group's lower part.
 */
export function adjustedBy(doc: Pick<Document, "nodes">, siblings: readonly string[], id: string): string[] {
  const i = siblings.indexOf(id);
  if (i < 0) return [];
  const n = doc.nodes[id];
  if (n?.clip) {
    const run = clipRuns(doc, siblings).find((r) => r.clipped.includes(id));
    if (run) return [run.base, ...run.clipped.slice(0, run.clipped.indexOf(id))];
  }
  return siblings.slice(0, i).filter((s) => !doc.nodes[s]?.clip || !!clipRuns(doc, siblings.slice(0, i)).find((r) => r.clipped.includes(s)));
}

export interface AdjustmentSpec {
  label: string;
  /** Short help shown in menus. */
  hint: string;
}

export const ADJUSTMENT_SPECS: Record<AdjustmentKind, AdjustmentSpec> = {
  brightnessContrast: { label: "Brightness/Contrast", hint: "Lighten, darken and add punch" },
  levels: { label: "Levels", hint: "Set black, white and midtone points" },
  curves: { label: "Curves", hint: "Precise tone curve" },
  exposure: { label: "Exposure", hint: "Exposure in stops, offset and gamma" },
  vibrance: { label: "Vibrance", hint: "Boost muted colours" },
  hsl: { label: "Hue/Saturation", hint: "Shift hue, saturation and lightness" },
  colorBalance: { label: "Colour Balance", hint: "Tint shadows, midtones and highlights" },
  blackWhite: { label: "Black & White", hint: "Monochrome with colour-channel mix" },
  photoFilter: { label: "Photo Filter", hint: "Warming or cooling lens filter" },
  invert: { label: "Invert", hint: "Negative image" },
  posterize: { label: "Posterize", hint: "Reduce tone levels" },
  threshold: { label: "Threshold", hint: "Pure black and white" },
  gradientMap: { label: "Gradient Map", hint: "Map tones to a gradient" },
};

export const ADJUSTMENT_KINDS = Object.keys(ADJUSTMENT_SPECS) as AdjustmentKind[];

const tone0 = () => ({ cyanRed: 0, magentaGreen: 0, yellowBlue: 0 });

/** Photoshop-like starting values (most are neutral; a few show an effect straight away). */
export function defaultAdjustment<K extends AdjustmentKind>(kind: K): AdjustmentOf<K> {
  const all: { [T in AdjustmentKind]: AdjustmentOf<T> } = {
    brightnessContrast: { kind: "brightnessContrast", brightness: 0, contrast: 0 },
    levels: { kind: "levels", levels: {} },
    curves: { kind: "curves", curves: {} },
    exposure: { kind: "exposure", exposure: 0, offset: 0, gamma: 1 },
    vibrance: { kind: "vibrance", vibrance: 0, saturation: 0 },
    hsl: { kind: "hsl", master: { hue: 0, saturation: 0, lightness: 0 }, hsl: {} },
    colorBalance: { kind: "colorBalance", shadows: tone0(), midtones: tone0(), highlights: tone0(), preserveLuminosity: true },
    blackWhite: { kind: "blackWhite", reds: 40, yellows: 60, greens: 40, cyans: 60, blues: 20, magentas: 80, tint: null },
    photoFilter: { kind: "photoFilter", color: "#ec8a00ff", density: 25, preserveLuminosity: true },
    invert: { kind: "invert" },
    posterize: { kind: "posterize", levels: 4 },
    threshold: { kind: "threshold", level: 128 },
    gradientMap: { kind: "gradientMap", stops: [{ offset: 0, color: "#000000ff" }, { offset: 1, color: "#ffffffff" }], reverse: false },
  };
  return structuredClone(all[kind]) as AdjustmentOf<K>;
}

/** True when an adjustment changes nothing (the renderer skips it). */
export function isNeutralAdjustment(p: AdjustmentParams): boolean {
  switch (p.kind) {
    case "brightnessContrast":
      return !p.brightness && !p.contrast;
    case "exposure":
      return !p.exposure && !p.offset && p.gamma === 1;
    case "vibrance":
      return !p.vibrance && !p.saturation;
    case "levels":
      return Object.values(p.levels).every((c) => !c || (c.inBlack === 0 && c.inWhite === 1 && c.gamma === 1 && c.outBlack === 0 && c.outWhite === 1));
    case "curves":
      return Object.values(p.curves).every((c) => !c || c.every(([x, y]) => x === y));
    case "hsl":
      return !p.master.hue && !p.master.saturation && !p.master.lightness && Object.values(p.hsl).every((s) => !s || (!s.hue && !s.saturation && !s.lightness));
    case "colorBalance":
      return [p.shadows, p.midtones, p.highlights].every((t) => !t.cyanRed && !t.magentaGreen && !t.yellowBlue);
    case "photoFilter":
      return p.density === 0;
    default:
      return false;
  }
}
